import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  TablePagination
} from "@mui/material";
import MoreVert from "@mui/icons-material/MoreVertOutlined";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TablePaginationActions from "../../components/TablePaginationAction";

const getStatusColor = (status) => {
  const statusColors = {
    "Em aberto": "#f39c12",
    "Atendido": "#27ae60",
    "Cancelado": "#e74c3c",
    "Em andamento": "#3498db",
  };
  return statusColors[status] || "#95a5a6";
};

export default function PurchaseOrderTable({
  filteredRows,
  isLoading,
  onRowClick,
  onNewOrder,
  page,
  onPageChange,
  onRowsPerPageChange,
  totalCount,
  tableContainerRef
}) {
  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={8} align="center">
        <Box sx={{ py: 6 }}>
          <ReceiptIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Nenhum pedido encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tente ajustar os filtros ou criar um novo pedido
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewOrder}
            disabled
            sx={{
              bgcolor: "#ff9800",
              "&:hover": { bgcolor: "#f57c00" }
            }}
          >
            Novo Pedido
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderLoadingState = () => (
    <TableRow>
      <TableCell colSpan={8} align="center">
        <Box sx={{ py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            Carregando pedidos...
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderTableRow = (row) => (
    <TableRow
      key={row.id}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
        cursor: "pointer",
        "&:hover": {
          bgcolor: "#fff3e0",
        },
      }}
      onClick={() => onRowClick(row)}
    >
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            src={row.supplierLogo}
            alt={row.contact_name}
            sx={{ width: 32, height: 32, mr: 1.5 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.contact_name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          #{row.numero}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CalendarMonthIcon sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }} />
          <Typography variant="body2">{row.date}</Typography>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" color="text.secondary">
          {row.email}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.note}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#2e7d32" }}>
          {parseFloat(row.unity_value || 0).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={row.status}
          size="small"
          sx={{
            backgroundColor: getStatusColor(row.status),
            color: "white",
            fontWeight: 500,
            fontSize: "0.75rem",
          }}
        />
      </TableCell>
      <TableCell align="center">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
          }}
          sx={{
            color: "#ff9800",
            "&:hover": { bgcolor: "#fff3e0" }
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer
        component={Paper}
        ref={tableContainerRef}
        sx={{
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          borderRadius: 3,
          border: "1px solid #e8e8e8",
          overflow: "hidden"
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="tabela de pedidos de compra">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <TableCell sx={{ fontWeight: 600, color: "#424242" }}>Fornecedor</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#424242" }}>Número</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#424242" }}>Data</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#424242" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#424242" }}>Observação</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: "#424242" }}>Total</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#424242" }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#424242" }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading 
              ? renderLoadingState()
              : filteredRows.length === 0 
                ? renderEmptyState()
                : filteredRows.map(renderTableRow)
            }
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, bgcolor: "white" }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          colSpan={3}
          count={totalCount}
          rowsPerPage={page.limit}
          page={page.page}
          slotProps={{
            select: {
              inputProps: { "aria-label": "Linhas por página" },
              native: true,
            },
          }}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Box>
    </Box>
  );
}